import { useAuthenticator } from "@aws-amplify/ui-react";
import { fetchAuthSession } from "aws-amplify/auth";
import { type FC, useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import * as characterApi from "../api/character";
import * as chatRoomApi from "../api/chat-room";
import * as llmApi from "../api/llm";
import { ChatRoomItem } from "../components/chat-room-item";
import { CreateChatRoomModal } from "../components/create-chat-room-modal";
import { CreateGroupItem } from "../components/create-group-item";
import { Header } from "../components/header";
import { ScrollArea } from "../components/ui/scroll-area";
import type * as types from "../lib/types";

export const ChatRoom: FC = () => {
  const params = useParams();
  const characterId = params.characterId as string;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [chatRooms, setChatRooms] = useState<types.ChatRoom[]>([]);
  const [models, setModels] = useState<llmApi.LLMModel[]>([]);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { user } = useAuthenticator((context) => [context.user]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const init = async () => {
      try {
        const authSession = await fetchAuthSession();
        const maybeToken = authSession.tokens?.idToken?.toString();
        if (!maybeToken) {
          toast.error("認証情報が見つかりません\nリロードして再度ログインしてください");
          return;
        }
        setToken(maybeToken);

        const maybeAccountId = user?.userId;
        if (!maybeAccountId) {
          toast.error("アカウント情報が見つかりません\nリロードして再度ログインしてください");
          return;
        }
        setAccountId(maybeAccountId);

        const { llm_models } = await llmApi.listLlmModels(maybeToken);
        if (llm_models.length === 0) {
          toast.error("LLMモデルが見つかりません");
          return;
        }
        setModels(llm_models);

        const { chat_rooms } = await chatRoomApi.listChatRooms(maybeToken, characterId);
        const roomsWithMainCharacter = await Promise.all(
          chat_rooms.map(async (room) => {
            const character = await characterApi.getCharacterInChatRoom(maybeToken, room.id, room.character_id);
            return {
              ...room,
              mainCharacter: character,
            };
          }),
        );

        setChatRooms(roomsWithMainCharacter);
        setError(null);
        toast.success("データ管理タグ一覧を取得しました");
      } catch (error) {
        console.error(error);
        setError("データ管理タグ一覧の取得に失敗しました");
        toast.error("データ管理タグ一覧の取得に失敗しました");
      }
    };

    init();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleCreateChatRoom = useCallback(
    async (chatRoomName: string, characterId: string, model: llmApi.LLMModel) => {
      if (!token) {
        toast.error("認証情報が見つかりません\nリロードして再度ログインしてください");
        return;
      }
      if (!accountId) {
        toast.error("アカウント情報が見つかりません\nリロードして再度ログインしてください");
        return;
      }

      try {
        const { chat_room_id } = await chatRoomApi.createChatRoom(token, {
          name: chatRoomName,
          character_id: characterId,
          llm_model_id: model.id,
        });
        if (!chat_room_id) {
          toast.error("データ管理タグの作成に失敗しました");
          return;
        }

        await chatRoomApi.joinChatRoom(token, chat_room_id, accountId);

        setIsCreateModalOpen(false);
        navigate(`/characters/${characterId}/chat-rooms/${chat_room_id}`);
        toast.success("データ管理タグを作成しました");
      } catch (error) {
        console.error("Failed to create chat room:", error);
        toast.error("データ管理タグの作成に失敗しました");
      }
    },
    [navigate, token],
  );

  const handleNextPhase = useCallback(
    async (chatRoomId: string, chatRoomName: string) => {
      if (!token) {
        toast.error("認証情報が見つかりません\nリロードして再度ログインしてください");
        return;
      }

      if (!accountId) {
        toast.error("アカウント情報が見つかりません\nリロードして再度ログインしてください");
        return;
      }

      try {
        const { chat_room_id, character_id, phase } = await chatRoomApi.nextPhase(token, accountId, chatRoomId);
        const character = await characterApi.getCharacterInChatRoom(token, chatRoomId, character_id);

        const newChatRoom: types.ChatRoom = {
          id: chat_room_id,
          name: chatRoomName,
          phase,
          mainCharacter: character,
        };

        setChatRooms((prevRooms) => [newChatRoom, ...prevRooms]);
        toast.success(`"${chatRoomName} (${phase})" が作成されました。`);
      } catch (error) {
        console.error(error);
        toast.error("データ管理タグの作成に失敗しました");
      }
    },
    [accountId, token],
  );

  return (
    <div className="h-screen flex flex-col bg-white">
      <Header />
      <div className="p-4 border-b">
        <h1 className="text-2xl font-bold mb-4">データ管理タグ一覧</h1>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {error ? (
            <div className="text-center p-4 text-red-500">
              <p>{error}</p>
            </div>
          ) : (
            <>
              <CreateGroupItem onClick={() => setIsCreateModalOpen(true)} />
              {chatRooms.map((room) => (
                <ChatRoomItem
                  key={room.id}
                  chatRoomId={room.id}
                  chatRoomName={room.name}
                  phase={room.phase}
                  onClick={() => navigate(`/characters/${characterId}/chat-rooms/${room.id}`)}
                  onNextPhase={() => handleNextPhase(room.id, room.name)}
                />
              ))}
            </>
          )}
        </div>
      </ScrollArea>
      <CreateChatRoomModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        characterId={characterId}
        onCreateChatRoom={handleCreateChatRoom}
        models={models}
      />
    </div>
  );
};
